import { useCallback, useEffect, useState, useMemo, Fragment } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import DrillDownModal from "@/components/DrillDownModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  TrendingDown, TrendingUp, Scale, BookOpen, FileSpreadsheet,
  Download, Plus, Trash2, ArrowDownUp, Sparkles, CheckCircle2, AlertCircle,
  CheckSquare, Square, ChevronDown, Building2, Tag, X, Users,
} from "lucide-react";
import { startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { toast } from "sonner";
import { DateField, fmtISO, downloadStream, CsvButton, BalancedBadge, RailSectionHeader } from "./_shared";

export function CashFlowReport({ currency }) {
  const [start, setStart] = useState(startOfMonth(new Date()));
  const [end, setEnd] = useState(endOfMonth(new Date()));
  const [rawData, setRawData] = useState(null);
  const [drill, setDrill] = useState(null);
  const { filterRails } = useCurrencyRail();
  const load = useCallback(async () => {
    const { data } = await api.get("/accounting/cash-flow", { params: { start: fmtISO(start), end: fmtISO(end) } });
    setRawData(data);
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  if (!rawData) return <div className="text-sm text-[var(--muted-text)]">Loading…</div>;
  const data = { ...rawData, by_currency: filterRails(rawData.by_currency || []) };

  const multi = Array.isArray(data.by_currency) && data.by_currency.length > 1;

  // iter 72o: turn every activity line into a drill-down trigger.
  const drillActivity = (kind, label, ccy) => setDrill({
    title: `${label} · ${ccy || currency}`,
    params: { kind, start: fmtISO(start), end: fmtISO(end) },
  });

  const line = (label, amount, ccy, weight = "normal", color = "", drillKind = null) => (
    <div
      className={`flex items-baseline justify-between py-2 border-b border-[var(--divider)] last:border-none ${weight === "bold" ? "font-semibold" : ""} ${drillKind ? "cursor-pointer hover:bg-[var(--hover)] rounded px-1 -mx-1" : ""}`}
      onClick={drillKind ? () => drillActivity(drillKind, label, ccy) : undefined}
      title={drillKind ? "Click to drill down" : undefined}
    >
      <span className={color}>
        {label}
        {drillKind && <span className="ml-1.5 text-[10px] text-[var(--muted-text)]">↗</span>}
      </span>
      <span className={`font-mono-data ${color}`}>{formatCurrency(amount, ccy)}</span>
    </div>
  );

  return (
    <div data-testid="cf-report" className="space-y-6">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex gap-3">
          <DateField label="From" value={start} onChange={setStart} testId="cf-start" />
          <DateField label="To" value={end} onChange={setEnd} testId="cf-end" />
        </div>
        <div className="flex items-center gap-2">
          {(multi ? data.by_currency.every((r) => r.reconciled) : data.reconciled) ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF4EE] text-[#2F6B4A] px-3 py-1 text-xs">
              <CheckCircle2 className="w-3.5 h-3.5" /> {multi ? "All rails reconciled" : "Reconciled with wallet balances"}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FCE7E9] text-[#B4283A] px-3 py-1 text-xs">
              <AlertCircle className="w-3.5 h-3.5" /> Reconciliation mismatch
            </span>
          )}
        </div>
      </div>

      {multi ? (
        /* Multi-currency: KPI stacks per rail — no summing across currencies */
        <div data-testid="cf-kpi-multi" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.by_currency.map((r) => (
            <div key={r.currency} data-testid={`cf-kpi-${r.currency}`} className="card-flat p-5 border-l-4"
                 style={{ borderLeftColor: r.currency === "USD" ? "#1E5A96" : "#4C956C" }}>
              <div className="flex items-baseline justify-between">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">{r.currency} rail</div>
                <div className={`text-xs ${r.reconciled ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
                  {r.reconciled ? "reconciled" : "off"}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Beginning</div>
                  <div data-testid={`cf-beginning-${r.currency}`} className="font-mono-data text-lg mt-1">{formatCurrency(r.beginning_cash, r.currency)}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] flex items-center gap-1">
                    <ArrowDownUp className="w-3 h-3" /> Net
                  </div>
                  <div data-testid={`cf-net-change-${r.currency}`} className={`font-mono-data text-lg mt-1 ${r.net_change_in_cash >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
                    {r.net_change_in_cash > 0 ? "+" : ""}{formatCurrency(r.net_change_in_cash, r.currency)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Ending</div>
                  <div data-testid={`cf-ending-${r.currency}`} className="font-mono-data text-lg mt-1">{formatCurrency(r.ending_cash, r.currency)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card-flat p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Beginning cash</div>
            <div data-testid="cf-beginning" className="font-mono-data text-xl mt-2">{formatCurrency(data.beginning_cash, currency)}</div>
          </div>
          <div className="card-flat p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] flex items-center gap-1.5">
              <ArrowDownUp className="w-3.5 h-3.5" /> Net change
            </div>
            <div data-testid="cf-net-change" className={`font-mono-data text-xl mt-2 ${data.net_change_in_cash >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
              {data.net_change_in_cash > 0 ? "+" : ""}{formatCurrency(data.net_change_in_cash, currency)}
            </div>
          </div>
          <div className="card-flat p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Ending cash</div>
            <div data-testid="cf-ending" className="font-mono-data text-xl mt-2">{formatCurrency(data.ending_cash, currency)}</div>
          </div>
        </div>
      )}

      {/* Per-currency rails — separates USD wallets from NGN wallets */}
      {multi && (
        <ConsolidatedRailCard
          testId="cf-consolidated"
          subtitle="flip the sidebar FX toggle to hide"
          rails={data.by_currency.map((r) => ({
            currency: r.currency,
            beginning_cash: r.beginning_cash,
            net_change_in_cash: r.net_change_in_cash,
            ending_cash: r.ending_cash,
          }))}
          fields={[
            { key: "beginning_cash", label: "Beginning cash" },
            { key: "net_change_in_cash", label: "Net change" },
            { key: "ending_cash", label: "Ending cash" },
          ]}
        />
      )}
      {multi && (
        <div className="card-flat overflow-hidden" data-testid="cf-by-currency">
          <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)] flex items-center gap-2">
            Per-currency rails
            <span className="text-[10px] normal-case tracking-normal text-[var(--muted-text)]">· each rail reconciles independently</span>
          </div>
          <table className="qb-table w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                <th className="px-5 py-2 font-medium">Currency</th>
                <th className="px-5 py-2 font-medium text-right">Beginning</th>
                <th className="px-5 py-2 font-medium text-right">Operating</th>
                <th className="px-5 py-2 font-medium text-right">Financing</th>
                <th className="px-5 py-2 font-medium text-right">Net change</th>
                <th className="px-5 py-2 font-medium text-right">Ending</th>
                <th className="px-5 py-2 font-medium text-right">Reconciled</th>
              </tr>
            </thead>
            <tbody>
              {data.by_currency.map((r) => (
                <tr key={r.currency} data-testid={`cf-ccy-row-${r.currency}`} className="border-b border-[var(--divider)] last:border-none">
                  <td className="px-5 py-2.5 font-mono-data text-xs">{r.currency}</td>
                  <td className="px-5 py-2.5 text-right font-mono-data">{formatCurrency(r.beginning_cash, r.currency)}</td>
                  <td className={`px-5 py-2.5 text-right font-mono-data ${r.operating.total >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>{formatCurrency(r.operating.total, r.currency)}</td>
                  <td className={`px-5 py-2.5 text-right font-mono-data ${r.financing.total >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>{formatCurrency(r.financing.total, r.currency)}</td>
                  <td className={`px-5 py-2.5 text-right font-mono-data font-medium ${r.net_change_in_cash >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
                    {r.net_change_in_cash > 0 ? "+" : ""}{formatCurrency(r.net_change_in_cash, r.currency)}
                  </td>
                  <td className="px-5 py-2.5 text-right font-mono-data">{formatCurrency(r.ending_cash, r.currency)}</td>
                  <td className="px-5 py-2.5 text-right text-xs">
                    {r.reconciled ? (
                      <span className="inline-flex items-center gap-1 text-[#2F6B4A]"><CheckCircle2 className="w-3 h-3" /> yes</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#B4283A]"><AlertCircle className="w-3 h-3" /> off</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Activity sections — per-rail when multi-currency, else mixed */}
      {multi ? (
        data.by_currency.map((r) => (
          <div key={`act-${r.currency}`} data-testid={`cf-activity-${r.currency}`}>
            <RailSectionHeader
              currency={r.currency}
              subtitle={`Beginning ${formatCurrency(r.beginning_cash, r.currency)} → Ending ${formatCurrency(r.ending_cash, r.currency)}`}
              testId={`cf-rail-header-${r.currency}`}
              right={
                <div className={`font-mono-data text-sm ${r.net_change_in_cash >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
                  Net {r.net_change_in_cash > 0 ? "+" : ""}{formatCurrency(r.net_change_in_cash, r.currency)}
                </div>
              }
            />
            <div className="card-flat overflow-hidden mt-2">
              <div className="px-5 pt-3 text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Operating</div>
              <div className="px-5">
                {line("Cash received from income", r.operating.cash_from_income, r.currency, "normal", "", "income")}
                {line("Cash paid for expenses", -r.operating.cash_paid_for_expenses, r.currency, "normal", "", "expense")}
                {line("Net cash from operating", r.operating.total, r.currency, "bold", r.operating.total >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]")}
              </div>
              <div className="px-5 pt-2 text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Financing</div>
              <div className="px-5 pb-3">
                {line("Cash moved to savings buckets", -r.financing.cash_reserved_to_savings, r.currency, "normal", "", "saving")}
                {line("Net cash from financing", r.financing.total, r.currency, "bold", r.financing.total >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]")}
              </div>
            </div>
          </div>
        ))
      ) : (
        <>
          <div className="card-flat overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Operating activities</div>
            <div className="px-5">
              {line("Cash received from income", data.operating.cash_from_income, currency, "normal", "", "income")}
              {line("Cash paid for expenses", -data.operating.cash_paid_for_expenses, currency, "normal", "", "expense")}
              {line("Net cash from operating", data.operating.total, currency, "bold", data.operating.total >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]")}
            </div>
          </div>

          <div className="card-flat overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Investing activities</div>
            <div className="px-5 py-3 text-sm text-[var(--muted-text)]">{data.investing.note || `Net investing cash flow: ${formatCurrency(data.investing.total, currency)}`}</div>
          </div>

          <div className="card-flat overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Financing activities</div>
            <div className="px-5">
              {line("Cash moved to savings buckets", -data.financing.cash_reserved_to_savings, currency, "normal", "", "saving")}
              {line("Net cash from financing", data.financing.total, currency, "bold", data.financing.total >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]")}
            </div>
          </div>

          <div className="card-flat p-5 bg-[var(--hover)]">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Reconciliation</div>
                <div className="text-xs text-[var(--muted-text)] mt-1">Beginning cash + Net change = Ending cash</div>
              </div>
              <div className="font-mono-data">
                {formatCurrency(data.beginning_cash, currency)} + {formatCurrency(data.net_change_in_cash, currency)} = <b>{formatCurrency(data.ending_cash, currency)}</b>
              </div>
            </div>
          </div>
        </>
      )}

      {data.unassigned?.note && (
        <div className="text-xs text-[var(--muted-text)]">
          Note: {data.unassigned.note}
        </div>
      )}
      <DrillDownModal
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        params={drill?.params}
        currency={currency}
      />
    </div>
  );
}
